export const ROUTE_FAMILY_TELEMETRY_EVENT = "ecotour:telemetry";

export const ROUTE_FAMILY_OPERATIONS = Object.freeze([
  "route_enter",
  "cache_status",
  "compact_ready",
  "compact_empty",
  "compact_failed",
  "deferred_ready",
  "deferred_empty",
  "deferred_failed",
  "route_return",
]);

const OPERATION_SET = new Set(ROUTE_FAMILY_OPERATIONS);
const COMPACT_TERMINALS = new Set(["compact_ready", "compact_empty", "compact_failed"]);
const ROUTE_DEFINITIONS = Object.freeze([
  [/^\/$|^\/home\/stays\/?$/, "home_stays", "landing"],
  [/^\/home\/experiences\/?$/, "home_experiences", "landing"],
  [/^\/home\/explore\/?$/, "home_explore", "landing"],
  [/^\/destinations\/?$/, "destination", "listing"],
  [/^\/destination\/[^/]+\/?$/, "destination", "detail"],
  [/^\/stays-camps\/?$/, "camp", "listing"],
  [/^\/stays-camps\/[^/]+\/[^/]+\/?$/, "camp", "detail"],
  [/^\/districts\/?$/, "district", "listing"],
  [/^\/district\/[^/]+\/?$/, "district", "detail"],
  [/^\/experiences\/?$/, "experience", "listing"],
  [/^\/experiences\/[^/]+\/[^/]+\/?$/, "experience", "detail"],
  [/^\/jungle-safaris\/?$/, "safari", "listing"],
  [/^\/jungle-safaris\/[^/]+\/[^/]+\/?$/, "safari", "detail"],
  [/^\/cruise-boating\/?$/, "cruise", "listing"],
  [/^\/cruise-boating\/[^/]+\/[^/]+\/?$/, "cruise", "detail"],
  [/^\/(?:treks-trails|track-trails)\/?$/, "trek", "listing"],
  [/^\/(?:treks-trails|track-trails)\/(?:[^/]+\/)?[^/]+\/?$/, "trek", "detail"],
  [/^\/packages\/?$/, "package", "listing"],
  [/^\/packages\/[^/]+\/?$/, "package", "detail"],
  [/^\/[^/]+\/[^/]+\/?$/, "general_experience", "detail"],
]);

const safeText = (value, maxLength = 80) => String(value || "")
  .replace(/[^a-z0-9._~-]/gi, "-")
  .slice(0, maxLength);

const normalizeDuration = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(3_600_000, parsed)) : 0;
};

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
const visitedRoutes = new Set();
let navigation = {
  pathname: "/",
  startedAt: now(),
  routeFamily: "other",
  routeKind: "other",
  returning: false,
  emitted: new Set(),
  routeReturnEmitted: false,
};

export const resolveRouteFamily = (value = "/") => {
  let pathname = String(value || "/").split(/[?#]/, 1)[0] || "/";
  try {
    if (/^https?:\/\//i.test(pathname)) pathname = new URL(pathname).pathname;
  } catch {
    pathname = "/";
  }
  const match = ROUTE_DEFINITIONS.find(([pattern]) => pattern.test(pathname));
  return {
    pathname,
    routeFamily: match?.[1] || "other",
    routeKind: match?.[2] || "other",
    monitored: Boolean(match),
  };
};

const dispatchRouteFamilyTelemetry = (detail) => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return false;
  window.dispatchEvent(new CustomEvent(ROUTE_FAMILY_TELEMETRY_EVENT, {
    detail: { event: "route_family", ...detail },
  }));
  return true;
};

export const beginRouteFamilyNavigation = (pathname = "/", { navigationType = "" } = {}) => {
  const descriptor = resolveRouteFamily(pathname);
  const returning = visitedRoutes.has(descriptor.pathname);
  visitedRoutes.add(descriptor.pathname);
  navigation = {
    ...descriptor,
    startedAt: now(),
    returning,
    emitted: new Set(),
    routeReturnEmitted: false,
  };
  if (!descriptor.monitored) return descriptor;
  dispatchRouteFamilyTelemetry({
    operation: "route_enter",
    metric: "route_navigation",
    routeFamily: descriptor.routeFamily,
    routeKind: descriptor.routeKind,
    navigationType: safeText(navigationType, 20),
    duration: 0,
  });
  return { ...descriptor, returning };
};

export const recordRouteFamilyMetric = (operation, data = {}) => {
  const normalizedOperation = String(operation || "").trim().toLowerCase();
  if (!OPERATION_SET.has(normalizedOperation)) return false;
  const descriptor = data.pathname ? resolveRouteFamily(data.pathname) : navigation;
  if (!descriptor.monitored) return false;
  const segment = safeText(data.segment || (descriptor.routeKind === "detail" ? "detail" : "inventory"), 40);
  const dedupeKey = `${normalizedOperation}:${segment}`;
  if (navigation.pathname === descriptor.pathname && navigation.emitted.has(dedupeKey)) return false;
  if (navigation.pathname === descriptor.pathname) navigation.emitted.add(dedupeKey);
  const duration = normalizeDuration(data.duration ?? (now() - navigation.startedAt));
  const inventoryState = ["ready", "empty", "failed", "retained", "unknown"].includes(data.inventoryState)
    ? data.inventoryState
    : "unknown";
  const metric = normalizedOperation.startsWith("compact_")
    ? "compact_ready"
    : normalizedOperation.startsWith("deferred_")
      ? "deferred_ready"
      : normalizedOperation === "route_return"
        ? "route_return"
        : normalizedOperation === "cache_status"
          ? "route_cache"
          : "route_navigation";
  const detail = {
    operation: normalizedOperation,
    metric,
    value: duration,
    duration,
    routeFamily: safeText(data.routeFamily || descriptor.routeFamily, 40),
    routeKind: safeText(data.routeKind || descriptor.routeKind, 20),
    segment,
    cacheStatus: ["hit", "miss", "stale", "bypass", "coalesced", "unknown"].includes(data.cacheStatus)
      ? data.cacheStatus
      : "",
    inventoryState,
    itemCount: Math.max(0, Math.min(100_000, Number(data.itemCount) || 0)),
    payloadBytes: Math.max(0, Math.min(100_000_000, Number(data.payloadBytes) || 0)),
    retained: Boolean(data.retained),
  };
  const accepted = dispatchRouteFamilyTelemetry(detail);
  if (
    accepted
    && navigation.pathname === descriptor.pathname
    && navigation.returning
    && !navigation.routeReturnEmitted
    && COMPACT_TERMINALS.has(normalizedOperation)
  ) {
    navigation.routeReturnEmitted = true;
    dispatchRouteFamilyTelemetry({
      ...detail,
      operation: "route_return",
      metric: "route_return",
      segment: "route-return",
    });
  }
  return accepted;
};

export const __resetRouteFamilyMonitoringForTests = () => {
  visitedRoutes.clear();
  navigation = {
    pathname: "/",
    startedAt: now(),
    routeFamily: "other",
    routeKind: "other",
    monitored: false,
    returning: false,
    emitted: new Set(),
    routeReturnEmitted: false,
  };
};

